import React from 'react'
import { LucideIcon, Info } from 'lucide-react'
import Card from './Card'
import Tooltip from './Tooltip'

export type KPIColorScheme = 'slate' | 'blue' | 'emerald' | 'amber' | 'purple' | 'indigo'

interface KPICardProps {
  label: string
  value: string | number
  icon: LucideIcon
  colorScheme: KPIColorScheme
  loading?: boolean
  tooltip?: string
}

const colorMap: Record<KPIColorScheme, {
  bg: string
  border: string
  hoverBorder: string
  text: string
  value: string
  icon: string
}> = {
  slate: {
    bg: 'from-white to-slate-50/40',
    border: 'border-slate-100',
    hoverBorder: 'hover:border-slate-200',
    text: 'text-slate-600',
    value: 'text-slate-900',
    icon: 'from-slate-600 to-slate-700'
  },
  blue: {
    bg: 'from-white to-blue-50/40',
    border: 'border-blue-100',
    hoverBorder: 'hover:border-blue-200',
    text: 'text-blue-600',
    value: 'text-blue-900',
    icon: 'from-blue-600 to-blue-700'
  },
  emerald: {
    bg: 'from-white to-emerald-50/40',
    border: 'border-emerald-100',
    hoverBorder: 'hover:border-emerald-200',
    text: 'text-emerald-600',
    value: 'text-emerald-900',
    icon: 'from-emerald-600 to-emerald-700'
  },
  amber: {
    bg: 'from-white to-amber-50/40',
    border: 'border-amber-100',
    hoverBorder: 'hover:border-amber-200',
    text: 'text-amber-600',
    value: 'text-amber-900',
    icon: 'from-amber-600 to-amber-700'
  },
  purple: {
    bg: 'from-white to-purple-50/40',
    border: 'border-purple-100',
    hoverBorder: 'hover:border-purple-200',
    text: 'text-purple-600',
    value: 'text-purple-900',
    icon: 'from-purple-600 to-purple-700'
  },
  indigo: {
    bg: 'from-white to-indigo-50/40',
    border: 'border-indigo-100',
    hoverBorder: 'hover:border-indigo-200',
    text: 'text-indigo-600',
    value: 'text-indigo-900',
    icon: 'from-indigo-600 to-indigo-700'
  }
}

const KPICard: React.FC<KPICardProps> = ({
  label,
  value,
  icon: Icon,
  colorScheme,
  loading = false,
  tooltip
}) => {
  const colors = colorMap[colorScheme]

  return (
    <Card
      className={`px-3 py-2 bg-gradient-to-br ${colors.bg} ${colors.border} ${colors.hoverBorder} hover:shadow-lg transition-all duration-300 group`}
    >
      <div className="flex items-center justify-between">
        <div>
          <h3 className={`text-xs font-medium ${colors.text} flex items-center gap-1`}>
            {label}
            {tooltip && (
              <Tooltip content={tooltip} placement="top" maxWidth={220}>
                <Info className="h-3 w-3 text-gray-400 hover:text-gray-600 cursor-help" />
              </Tooltip>
            )}
          </h3>
          <p className={`text-base font-bold ${colors.value}`}>
            {loading ? '...' : value}
          </p>
        </div>
        <div
          className={`p-1.5 rounded-md shadow-md bg-gradient-to-br ${colors.icon} group-hover:scale-110 transition-transform duration-300`}
        >
          <Icon className="h-3.5 w-3.5 text-white" />
        </div>
      </div>
    </Card>
  )
}

export default KPICard
